import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";

import { signInWithEmailAndPassword } from "firebase/auth";
import { 
  doc, 
  getDoc, 
  collection, 
  addDoc, 
  serverTimestamp 
} from "firebase/firestore";

import { auth, db } from "../firebase/firebase";

export default function Login() {
  const [loginType, setLoginType] = useState("admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const userRef = useRef(null);

  useEffect(() => {
    userRef.current?.focus();
  }, []);

  // --- HELPER FUNCTION: RECORD STORE LOGIN ---
  // Now accepts storeId and email directly to avoid unnecessary/failing queries
  const recordStoreLogin = async (uid, storeId, userEmail) => {
    if (!storeId) {
      console.warn("No storeId provided for login tracking.");
      return;
    }

    try {
      // 1. Fetch public IP address
      let ipAddress = "Unknown IP";
      try {
        const ipRes = await fetch("https://api.ipify.org?format=json");
        const ipData = await ipRes.json();
        ipAddress = ipData.ip;
      } catch (ipErr) {
        console.warn("Could not fetch IP address", ipErr);
      }

      // 2. Format current date & time
      const now = new Date();
      const loginRecord = {
        loginId: userEmail,
        authUid: uid,
        ipAddress: ipAddress,
        fullTime: now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
        date: String(now.getDate()).padStart(2, '0'),
        month: String(now.getMonth() + 1).padStart(2, '0'),
        monthName: now.toLocaleString('default', { month: 'long' }),
        year: String(now.getFullYear()),
        fullDateString: now.toISOString().split('T')[0], // Format: YYYY-MM-DD
        timestamp: serverTimestamp() // Firebase server time for accurate sorting
      };

      // 3. Save directly to stores/{storeId}/loginreport
      await addDoc(collection(db, "stores", storeId, "loginreport"), loginRecord);
      console.log("Login report successfully pushed to Firestore.");

    } catch (error) {
      console.error("Failed to record login analytics:", error);
    }
  };

  const handleLogin = async () => {
    try {
      if (!email || !password) {
        alert("Please enter email and password");
        return;
      }

      setLoading(true);

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      const uid = userCredential.user.uid;
      const userDoc = await getDoc(doc(db, "users", uid));

      if (!userDoc.exists()) {
        alert("User profile not found");
        return;
      }

      const userData = userDoc.data();

      localStorage.setItem("uid", uid);
      localStorage.setItem("role", userData.role);
      localStorage.setItem("storeId", userData.storeId || "");

      if (loginType === "admin" && userData.role !== "SUPER_ADMIN") {
        alert("Not a Super Admin account");
        return;
      }

      if (loginType === "store" && userData.role !== "STORE_ADMIN") {
        alert("Not a Store Admin account");
        return;
      }

      // --- ROUTING & TRACKING EXECUTION ---
      if (userData.role === "SUPER_ADMIN") {
        navigate("/dashboard");
        return;
      }

      if (userData.role === "STORE_ADMIN") {
        // Trigger the tracking function using the storeId directly from the user's profile
        await recordStoreLogin(uid, userData.storeId, email);
        navigate("/store/dashboard");
        return;
      }

      alert("Invalid role");
    } catch (error) {
      console.error(error);

      switch (error.code) {
        case "auth/user-not-found":
          alert("User not found");
          break;
        case "auth/wrong-password":
          alert("Wrong password");
          break;
        case "auth/invalid-credential":
          alert("Invalid email or password");
          break;
        default:
          alert(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-4">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-8">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-600 p-4 rounded-full">
              <ShieldCheck className="text-white" size={34} />
            </div>
          </div>

          <h2 className="text-center text-3xl font-bold text-white">
            Shruti Eye Care
          </h2>

          <p className="text-center text-slate-300 mt-2 mb-6">
            {loginType === "admin"
              ? "Super Admin Portal"
              : "Store Admin Portal"}
          </p>

          {/* Portal Selector */}
          <div className="flex bg-white/10 p-1 rounded-xl mb-4">
            <button
              onClick={() => setLoginType("admin")}
              className={`flex-1 py-2 rounded-lg ${
                loginType === "admin"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              Super Admin
            </button>

            <button
              onClick={() => setLoginType("store")}
              className={`flex-1 py-2 rounded-lg ${
                loginType === "store"
                  ? "bg-cyan-600 text-white shadow-sm"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              Store Admin
            </button>
          </div>

          {/* Email */}
          <input
            ref={userRef}
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-4 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white outline-none focus:border-cyan-400 transition-colors"
          />

          {/* Password */}
          <div className="relative mb-5">
            <input
              type={showPass ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white outline-none focus:border-cyan-400 transition-colors"
            />

            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="absolute right-4 top-3 text-white/70 hover:text-white transition-colors"
            >
              {showPass ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <motion.button
            whileTap={{ scale: 0.96 }}
            whileHover={{ scale: 1.02 }}
            disabled={loading}
            onClick={handleLogin}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white py-3 rounded-xl font-semibold shadow-lg shadow-blue-900/50 disabled:opacity-70 disabled:cursor-not-allowed transition-all"
          >
            {loading ? "Authenticating & Tracking..." : "Login Securely"}
          </motion.button>

          <p className="text-center text-xs text-slate-400 mt-6">
            © 2026 Shruti Eye Care & Opticals
          </p>
        </div>
      </motion.div>
    </div>
  );
}